<template>
  <scpm-popover
    :width="width"
    right="0"
    :open="isOpenPopover && listSearch.length > 0"
    @close="closePopover"
    class="search-text"
  >
    <template #target>
      <div id="scpm-search-field">
        <lego-text-field
          searchable
          disableInputMutation
          v-model="searchValue"
          @update:modelValue="changeSearchText"
          @focus="onFocus"
          :width="width"
          @enter="onSearch()"
          @click-icon="onSearch()"
          @clear="onClear"
          ref="textField"
          :placeholder="getPlaceholder"
          :counter="100"
          :class="{ 'not-text-value': !searchValue }"
          @change="onChangeKeyword"
        />
        <span ref="prefix" class="prefix" :class="{ 'search-forcus': focusing }">{{
          prefixText
        }}</span>
      </div>
    </template>
    <div class="auto-complete">
      <div
        v-for="(item, index) in listSearch"
        :key="index"
        class="auto-complete__items"
        :class="{ 'auto-complete__items--active': checkActiveComplete(item) }"
        @click="onSearchComplete(item)"
      >
        <div class="search-field">{{ labelTarget ? labelTarget : item.label }} :</div>
        <div class="search-text">{{ searchValue }}</div>
      </div>
    </div>
  </scpm-popover>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, defineExpose, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

//props
const props = defineProps({
  schema: {
    type: Object,
    default: () => {}
  },
  model: {
    type: Object,
    default: () => {}
  },
  searchFieldActive: {
    type: Object
  },
  items: {
    type: Array,
    default: []
  },
  searchTarget: {
    type: Boolean,
    default: false
  },
  width: {
    type: [String, Number],
    default: 300
  },
  mutateInputSearch: {
    type: Function
  },
  placeholder: {
    type: String
  },
  labelTarget: {
    type: String,
    default: ''
  },
});

//emits
const emits = defineEmits(['search', 'reset', 'clear', 'changeKeyword']);

const focusing = ref(false);
const searchValue = ref('');
const prefix = ref();
const textField = ref();
const { t } = useI18n();

// function
const closePopover = () => {
  if (focusing.value) {
    focusing.value = false;
  }
};

const getPlaceholder = computed(() => {
  if (props.placeholder) {
    return props.placeholder;
  }
  return t('searchLabel.placeholderSearchText');
});

const listSearch = computed(() => {
  const list = props.items || [];
  if (props.searchTarget) {
    return list.filter((item) => item.searchActive);
  }
  return list.filter((item) => item.type === 'input');
});

const checkActiveComplete = (field) => {
  if (!props.searchFieldActive) {
    return field.id === listSearch.value[0].id;
  }
  return field.id === props.searchFieldActive.id;
};

const isOpenPopover = computed(() => {
  return focusing.value && searchValue.value.trim().length > 0;
});

const prefixText = computed(() => {
  try {
    if (props.searchFieldActive.label.trim().length > 0) {
      return `${props.searchFieldActive.label.trim()} :`;
    }
  } catch (error) {
    return;
  }
});

const paddingLeftInput = computed(() => {
  if (focusing.value || !prefixText.value) {
    return '12px';
  }
  if (prefix.value && prefixText.value) {
    return prefix.value.clientWidth + 14 + 'px';
  }
  return '12px';
});

const changeSearchText = (value) => {
  if (value.trim().length) {
    focusing.value = true;
  }
};

const onClear = () => {
  emits('clear');
};

const onSearch = async () => {
  const item = props.searchFieldActive ? props.searchFieldActive : listSearch.value[0];
  emits('search', item, searchValue.value.trim());
  const input = textField.value.$refs.input;
  input.blur();
  closePopover();
};

const onSearchComplete = (item) => {
  emits('search', item, searchValue.value);
  closePopover();
};

const onFocus = () => {
  focusing.value = true;
};

const clearSearchValue = () => {
  searchValue.value = '';
};

const onChangeKeyword = (value) => {
  emits('changeKeyword', searchValue.value);
}

const setKeywordSearch = (val) => {
  searchValue.value = val;
  nextTick(() => {
    closePopover();
  })
}

defineExpose({
  clearSearchValue,
  setKeywordSearch
});
</script>

<style scoped lang="scss">
.search-text {
  :deep(.lego-text-field) {
    z-index: 101;

    .lego-text-field__message {
      display: none;
    }

    &:hover {
      .lego-text-field__input.lego-text-field__input--with-icon {
        padding-right: 50px;
      }
    }

    .lego-text-field__input {
      padding-left: v-bind(paddingLeftInput);
      padding-right: 30px;

      &.lego-text-field__input--with-icon {
        &:focus {
          padding-right: 50px;
        }
      }
    }
  }

  :deep(.lego-text-field.not-text-value) {
    .lego-text-field__input {
      padding-right: 30px !important;
    }
  }

  :deep(.scpm-popover__main) {
    border-radius: 0;
  }

  .auto-complete {
    &__items {
      width: 100%;
      min-height: 34px;
      display: flex;
      align-items: center;
      padding: 0 10px;
      cursor: pointer;

      &--active {
        background-color: #eef2fc;
      }

      &:hover {
        background-color: #eef2fc;
      }
    }
  }

  .prefix {
    position: absolute;
    left: 12px;
    top: 6px;
    pointer-events: none;
    z-index: 102;

    &.search-forcus {
      visibility: hidden;
    }
  }

  .search-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-primary);
    white-space: nowrap;
    flex: 1;
    margin-left: 5px;
  }
}
</style>
